import {LangKeyType} from '../../../../../provider/locale/translation/type';
import {ReviewsAndAnswersStatisticsUnitMeasurementEnum} from '../../../../../service/reviews-and-answers-statistics/reviews-and-answers-statistics';

export const averageResponseTimeTimeUnitTranslationMap: Record<
    ReviewsAndAnswersStatisticsUnitMeasurementEnum,
    LangKeyType
> = {
    [ReviewsAndAnswersStatisticsUnitMeasurementEnum.Days]: 'REVIEW_AND_ANSWER_STATISTICS__TIME_UNIT_DAY',
    [ReviewsAndAnswersStatisticsUnitMeasurementEnum.Hours]: 'REVIEW_AND_ANSWER_STATISTICS__TIME_UNIT_HOUR',
    [ReviewsAndAnswersStatisticsUnitMeasurementEnum.Minutes]: 'REVIEW_AND_ANSWER_STATISTICS__TIME_UNIT_MINUTE',
};

export type AverageResponseTimePartType = {
    count: number;
    unitStringKey: LangKeyType;
};

const SECONDS_IN_MINUTE = 60;
const SECONDS_IN_HOUR = 60 * 60;

type TimeUnitScaleType = {
    // Amount of seconds in the trailing (smaller) unit.
    smallSize: number;
    // How many trailing units fit into one leading unit.
    ratio: number;
    largeUnitStringKey: LangKeyType;
    smallUnitStringKey: LangKeyType;
};

// For the unit selected in the switcher, the value is rendered as a two-part
// "leading + trailing" duration, e.g. Hours -> hours + minutes ("4 ч 54 м").
const averageResponseTimeUnitScaleMap: Record<ReviewsAndAnswersStatisticsUnitMeasurementEnum, TimeUnitScaleType> = {
    [ReviewsAndAnswersStatisticsUnitMeasurementEnum.Days]: {
        smallSize: SECONDS_IN_HOUR,
        ratio: 24,
        largeUnitStringKey: 'REVIEW_AND_ANSWER_STATISTICS__TIME_UNIT_DAY__SHORT',
        smallUnitStringKey: 'REVIEW_AND_ANSWER_STATISTICS__TIME_UNIT_HOUR__SHORT',
    },
    [ReviewsAndAnswersStatisticsUnitMeasurementEnum.Hours]: {
        smallSize: SECONDS_IN_MINUTE,
        ratio: 60,
        largeUnitStringKey: 'REVIEW_AND_ANSWER_STATISTICS__TIME_UNIT_HOUR__SHORT',
        smallUnitStringKey: 'REVIEW_AND_ANSWER_STATISTICS__TIME_UNIT_MINUTE__SHORT',
    },
    [ReviewsAndAnswersStatisticsUnitMeasurementEnum.Minutes]: {
        smallSize: 1,
        ratio: 60,
        largeUnitStringKey: 'REVIEW_AND_ANSWER_STATISTICS__TIME_UNIT_MINUTE__SHORT',
        smallUnitStringKey: 'REVIEW_AND_ANSWER_STATISTICS__TIME_UNIT_SECOND__SHORT',
    },
};

/**
 * Splits a duration (in seconds) into a two-part "leading + trailing" representation
 * for the selected time unit, e.g. Hours -> "4 ч 54 м".
 * Zero leading/trailing parts are dropped; a zero duration collapses to "0 <leading unit>".
 */
export function getAverageResponseTimeParts(
    seconds: number,
    timeUnit: ReviewsAndAnswersStatisticsUnitMeasurementEnum
): Array<AverageResponseTimePartType> {
    const {smallSize, ratio, largeUnitStringKey, smallUnitStringKey} = averageResponseTimeUnitScaleMap[timeUnit];

    const totalSmall = Math.round(seconds / smallSize);
    const largeCount = Math.floor(totalSmall / ratio);
    const smallCount = totalSmall % ratio;

    const parts: Array<AverageResponseTimePartType> = [];

    if (largeCount > 0) {
        parts.push({count: largeCount, unitStringKey: largeUnitStringKey});
    }

    if (smallCount > 0) {
        parts.push({count: smallCount, unitStringKey: smallUnitStringKey});
    }

    if (parts.length === 0) {
        parts.push({count: 0, unitStringKey: largeUnitStringKey});
    }

    return parts;
}
